import {Button} from '@wandb/components';
import * as Dialog from '@wandb/weave/components/Dialog';
import {TextField} from '@wandb/weave/components/Form/TextField';
import React, {useState} from 'react';

import {useWorkspaceRefsContext} from '../../components/Workspace/WorkspaceRefsContext';
import {useUpdateRun} from '../../state/runs/hooks';
import {setCustomRunName as setCustomRunNameAction} from '../../state/views/customRunNames/actions';
import {useMaybeViewAction, usePartMaybe} from '../../state/views/hooks';
import {getCustomRunName} from '../../util/runs';

type RenameRunDialogProps = {
  runId: string;
  runName: string;
  runDisplayName: string;
  closeDialog: () => void;
};

export const RenameRunDialog = ({
  runId,
  runName,
  runDisplayName,
  closeDialog,
}: RenameRunDialogProps) => {
  const updateRun = useUpdateRun();
  const {customRunNamesRef} = useWorkspaceRefsContext();
  const customRunNames = usePartMaybe(customRunNamesRef);
  const customRunName = getCustomRunName(runName, false, customRunNames) ?? '';
  const setCustomRunName = useMaybeViewAction(
    customRunNamesRef,
    setCustomRunNameAction
  );

  const [newDisplayName, setNewDisplayName] = useState(runDisplayName);
  const [newCustomName, setNewCustomName] = useState(customRunName);

  const onSave = () => {
    if (newDisplayName !== runDisplayName) {
      updateRun(runId, runName, {displayName: newDisplayName});
    }
    if (newCustomName !== customRunName) {
      setCustomRunName(runName, newCustomName);
    }
    closeDialog();
  };

  return (
    <Dialog.Root open onOpenChange={closeDialog}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content className="w-[500px] rounded-lg">
          <Dialog.Title>Edit run name</Dialog.Title>
          <Dialog.Description className="text-moon-700 dark:text-moon-300">
            Edit the run name for the entire project, or add a display name for
            this workspace only.
          </Dialog.Description>
          <div className="mt-16 mb-24 flex flex-col gap-12">
            <div className="flex flex-col gap-4">
              <label htmlFor="project-run-name" className="font-semibold">
                Run name
              </label>
              <TextField
                id="project-run-name"
                value={newDisplayName}
                onChange={setNewDisplayName}
                placeholder="Choose a run name"
                isContainerNightAware
              />
            </div>
            <div className="flex flex-col gap-4">
              <label htmlFor="workspace-run-name" className="font-semibold">
                Display name (this workspace)
              </label>
              <TextField
                id="workspace-run-name"
                value={newCustomName}
                onChange={setNewCustomName}
                placeholder={newDisplayName || 'Choose a display name'}
                isContainerNightAware
              />
            </div>
          </div>
          <div className="flex justify-between gap-16 items-center">
            <div className="flex gap-8">
              <Button isDisabled={!newDisplayName} onPress={onSave}>
                Save
              </Button>
              <Button variant="secondary" onPress={closeDialog}>
                Cancel
              </Button>
            </div>
            <span className="text-moon-500">Unique run ID: {runName}</span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
